import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';
import { type SessionDetailDto, sessionDetailDtoSchema } from '../schemas/sessionSchemas';
import { sessionKeys } from './sessionKeys';

interface VerifyStepArgs {
  stepId: string;
  // Raw verification request body (for PAN: { pan_number, pan_holder_name, dob }).
  input: Record<string, unknown>;
  // Optional: call only this API binding (session UI drives the priority
  // fallback). Omit to let the backend run the full fallback chain.
  catalogueEntryId?: string;
}

// Submits one step's input → backend calls the real SignCare API → returns the
// updated session. We push the fresh detail straight into the cache so the
// screen updates immediately, and refresh the list (status/completion changed).
export function useVerifyStep(sessionId: string | undefined) {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);
  // Non-null in the anonymous public-link run — swaps the endpoint + cache path.
  const publicRun = usePublicRun();

  return useMutation({
    mutationFn: async ({ stepId, input, catalogueEntryId }: VerifyStepArgs) => {
      // 90s timeout: this triggers real SignCare KYC provider calls, and with
      // priority fallback the backend may call two providers in sequence.
      //
      // A public run always has its session (and token) already — it was created
      // when the applicant's mobile number was verified — so there is one public
      // path, not a session-less variant.
      const path = publicRun
        ? `api/v1/public/sessions/${publicRun.token}/steps/${stepId}/verify`
        : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/verify`;
      const client = publicRun ? publicApiClient : apiClient;
      const searchParams: Record<string, string> = {};
      if (catalogueEntryId) searchParams.catalogueEntryId = catalogueEntryId;
      const raw = await client
        .post(path, {
          json: input,
          searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
          timeout: 90_000,
        })
        .json();
      return sessionDetailDtoSchema.parse(raw);
    },
    onSuccess: (data: SessionDetailDto) => {
      // Public mode has no tenant cache — hand the fresh session to the runner.
      if (publicRun) {
        publicRun.onSessionUpdate(data);
        return;
      }
      if (sessionId) qc.setQueryData(sessionKeys.detail(sessionId), data);
      qc.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}
